import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  Box,
  Chip,
  Tooltip,
  type SxProps,
  type Theme,
} from "@mui/material";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { brainTokens } from "../design/brainTheme";
import { slugifyHeading, splitSections, type PlanSection } from "../lib/plan-sections";
import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type MouseEvent,
  type ReactNode,
} from "react";
import { getObjectText, getJiraIssueDetail, type JiraIssueDetail } from "../lib/brain-api";
import { documentLink, hasDocumentVisuals, visualLanguage } from "../lib/document-visuals";
import { VisualBlock } from "./document/VisualBlock";

const c = brainTokens.color;

const JIRA_KEY_RE = /\b([A-Z][A-Z0-9]+-\d+)\b/g;
const WIKI_LINK_RE = /\[\[([^\]]+)\]\]/g;

/** Shared reading-column typography — Plan tab + Library must stay aligned. */
export const markdownReadingSx: SxProps<Theme> = {
  minWidth: 0,
  fontFamily: brainTokens.font.sans,
  "& h1": { fontSize: 28, fontWeight: 700, letterSpacing: "-.02em", mt: 0, mb: 1.5, color: c.ink },
  "& h2": {
    fontSize: 18,
    fontWeight: 650,
    mt: 3,
    mb: 1,
    color: c.ink,
    scrollMarginTop: 80,
  },
  "& h3": { fontSize: 15, fontWeight: 600, mt: 2, mb: 0.75, color: c.ink2 },
  "& p": { fontSize: 14.5, lineHeight: 1.65, color: c.ink3, mb: 1.25 },
  "& ul, & ol": { pl: 2.5, mb: 1.25, color: c.ink3, fontSize: 14.5, lineHeight: 1.6 },
  "& code": {
    fontFamily: brainTokens.font.mono,
    fontSize: 12.5,
    background: c.panelBg2,
    px: 0.5,
    borderRadius: 0.5,
  },
  "& pre": {
    background: c.panelBg2,
    p: 1.5,
    borderRadius: 1,
    overflow: "auto",
    mb: 1.5,
  },
  "& blockquote": {
    borderLeft: `2px solid ${c.border}`,
    pl: 1.5,
    ml: 0,
    color: c.muted2,
    "& p": { color: c.muted2 },
  },
  "& a": { color: c.tealInk, textDecoration: "none", "&:hover": { textDecoration: "underline" } },
  "& table": {
    borderCollapse: "collapse",
    width: "100%",
    display: "block",
    overflowX: "auto",
    fontSize: 13.5,
    mb: 1.5,
  },
  "& th, & td": {
    border: `1px solid ${c.borderLight}`,
    p: "7px 10px",
    textAlign: "left",
    verticalAlign: "top",
  },
  "& th": { background: c.panelBg2, fontWeight: 650, color: c.ink2 },
  "& hr": { border: 0, borderTop: `1px solid ${c.borderLight}`, my: 2 },
};

export interface PlanFrontmatter {
  owner?: string;
  due?: string;
  reviewers?: string[];
  scope?: string;
  [key: string]: unknown;
}

/** Strip YAML frontmatter; return body + parsed fields (simple key: value). */
export function parsePlanFrontmatter(raw: string): { body: string; meta: PlanFrontmatter | null } {
  const m = /^---\r?\n([\s\S]*?)\r?\n---\r?\n([\s\S]*)$/.exec(raw);
  if (!m) return { body: raw, meta: null };
  const yaml = m[1]!;
  const body = m[2]!;
  const meta: PlanFrontmatter = {};
  for (const line of yaml.split(/\r?\n/)) {
    const kv = /^([A-Za-z0-9_]+):\s*(.*)$/.exec(line);
    if (!kv) continue;
    const key = kv[1]!;
    let val = kv[2]!.trim();
    if (val === "[]") {
      meta[key] = [];
      continue;
    }
    if (val.startsWith("[") && val.endsWith("]")) {
      meta[key] = val
        .slice(1, -1)
        .split(",")
        .map((s) => s.trim().replace(/^["']|["']$/g, ""))
        .filter(Boolean);
      continue;
    }
    meta[key] = val.replace(/^["']|["']$/g, "");
  }
  return { body, meta: Object.keys(meta).length ? meta : {} };
}

/** Rewrite [[wiki]] and leave Jira keys for the text renderer. */
function preprocessWikiLinks(md: string): string {
  return md.replace(WIKI_LINK_RE, (_full, inner: string) => {
    const parts = inner.split("|").map((s) => s.trim());
    const target = parts.length > 1 ? parts[1]! : parts[0]!;
    const label = parts[0]!;
    return `[${label}](wiki:${encodeURIComponent(target)})`;
  });
}

function JiraKeyChip({ issueKey }: { issueKey: string }) {
  const [detail, setDetail] = useState<JiraIssueDetail | null | undefined>();

  useEffect(() => {
    let cancelled = false;
    setDetail(undefined);
    void getJiraIssueDetail(issueKey)
      .then((d) => {
        if (!cancelled) setDetail(d);
      })
      .catch(() => {
        if (!cancelled) setDetail(null);
      });
    return () => {
      cancelled = true;
    };
  }, [issueKey]);

  const status = detail?.status ?? (detail === null ? 'Status unavailable' : 'Loading…');
  const title = detail?.summary?.trim();
  // Title-first; key secondary (falls back to key · status while loading / on miss).
  const label = title ? `${title} · ${issueKey}` : `${issueKey} · ${status}`;
  const tone =
    detail?.statusCategory === "done"
      ? { bg: c.successBg, fg: c.successText }
      : detail?.statusCategory === "indeterminate"
        ? { bg: c.tealTint, fg: c.tealInk }
        : { bg: c.panelBg2, fg: c.ink2 };

  return (
    <Tooltip describeChild title={title ? `${issueKey} · ${status}` : issueKey} placement="top">
      <Chip
        component="a"
        href={detail?.url ?? `https://aeronetnz.atlassian.net/browse/${encodeURIComponent(issueKey)}`}
        target="_blank"
        rel="noopener noreferrer"
        size="small"
        label={label}
        clickable
        sx={{
          height: 22,
          maxWidth: 280,
          fontSize: 11.5,
          fontFamily: title ? brainTokens.font.sans : brainTokens.font.mono,
          fontWeight: 600,
          background: tone.bg,
          color: tone.fg,
          mx: 0.25,
          verticalAlign: "middle",
          "& .MuiChip-label": { px: 0.85, overflow: "hidden", textOverflow: "ellipsis" },
        }}
      />
    </Tooltip>
  );
}

function WikiFilePreview({ fileKey, label }: { fileKey: string; label: string }) {
  const [preview, setPreview] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    if (!/\.(md|html?)$/i.test(fileKey)) return;
    void getObjectText(fileKey)
      .then((t) => {
        if (!cancelled) setPreview(t.slice(0, 400));
      })
      .catch(() => {
        if (!cancelled) setPreview(null);
      });
    return () => {
      cancelled = true;
    };
  }, [fileKey]);

  return (
    <Tooltip
      title={
        preview ? (
          <Box sx={{ whiteSpace: "pre-wrap", fontSize: 12, maxWidth: 320, fontFamily: brainTokens.font.mono }}>
            {preview}
            {preview.length >= 400 ? "…" : ""}
          </Box>
        ) : (
          label
        )
      }
      placement="top"
    >
      <Box component="span" sx={{ color: c.tealInk, cursor: "pointer", textDecoration: "underline dotted" }}>
        {label}
      </Box>
    </Tooltip>
  );
}

function enrichTextNodes(
  children: ReactNode,
  projectId: string | undefined,
  onOpenFile: ((key: string) => void) | undefined
): ReactNode {
  if (!projectId) return children;
  return mapChildren(children, (text) => {
    const parts: ReactNode[] = [];
    let last = 0;
    const re = new RegExp(JIRA_KEY_RE.source, "g");
    let m: RegExpExecArray | null;
    while ((m = re.exec(text)) !== null) {
      if (m.index > last) parts.push(text.slice(last, m.index));
      parts.push(<JiraKeyChip key={`${m[1]}-${m.index}`} issueKey={m[1]!} />);
      last = m.index + m[0].length;
    }
    if (last < text.length) parts.push(text.slice(last));
    return parts.length ? <>{parts}</> : text;
  });
}

function mapChildren(node: ReactNode, mapText: (t: string) => ReactNode): ReactNode {
  if (typeof node === "string") return mapText(node);
  if (Array.isArray(node)) return node.map((n, i) => <span key={i}>{mapChildren(n, mapText)}</span>);
  return node;
}

function FrontmatterHeader({ meta }: { meta: PlanFrontmatter }) {
  const reviewers = Array.isArray(meta.reviewers)
    ? meta.reviewers.join(", ")
    : typeof meta.reviewers === "string"
      ? meta.reviewers
      : "";
  const rows: Array<{ label: string; value: string }> = [];
  if (meta.owner) rows.push({ label: "Owner", value: String(meta.owner) });
  if (meta.due) rows.push({ label: "Due", value: String(meta.due) });
  if (reviewers) rows.push({ label: "Reviewers", value: reviewers });
  if (meta.scope) rows.push({ label: "Scope", value: String(meta.scope) });
  if (!rows.length) return null;
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        columnGap: 2,
        rowGap: 0.75,
        mb: 2.5,
        pb: 2,
        borderBottom: `1px solid ${c.borderLight}`,
        fontSize: 13.5,
      }}
    >
      {rows.map((r) => (
        <Box key={r.label} sx={{ display: "contents" }}>
          <Box sx={{ color: c.muted2, fontWeight: 600, letterSpacing: "0.04em", textTransform: "uppercase", fontSize: 11 }}>
            {r.label}
          </Box>
          <Box sx={{ color: c.ink2, fontWeight: 500 }}>{r.value || "—"}</Box>
        </Box>
      ))}
    </Box>
  );
}

/**
 * Shared calm reading-width markdown renderer used by the Plan tab and the
 * Library document view. Do not fork — both surfaces must stay visually aligned.
 */
export function MarkdownDocument({
  content,
  maxWidth = 740,
  sectionAttr = "data-md-section",
  /** When true, H2 id equals the section slug (plan-tab anchors). */
  lockH2ToSectionSlug = false,
  onMouseUp,
  wrapSection,
  sx,
  projectId,
  onOpenFile,
  documentKey,
}: {
  content: string;
  maxWidth?: number;
  sectionAttr?: string;
  lockH2ToSectionSlug?: boolean;
  onMouseUp?: (e: MouseEvent) => void;
  wrapSection?: (section: PlanSection, body: ReactNode) => ReactNode;
  sx?: SxProps<Theme>;
  /** Enables wiki-links, relative links → files, and Jira key chips. */
  projectId?: string;
  onOpenFile?: (fileKey: string) => void;
  /** Store key for resolving relative links in this document's own scope. */
  documentKey?: string;
}) {
  const navigate = useNavigate();
  const used = useRef(new Set<string>());
  used.current = new Set();

  const { body, meta } = useMemo(() => parsePlanFrontmatter(content), [content]);
  const prepared = useMemo(() => preprocessWikiLinks(body), [body]);
  const sections = useMemo(() => splitSections(prepared), [prepared]);
  const baseKey = documentKey ?? (projectId ? `projects/${projectId}/plan.md` : undefined);

  const openDocumentLink = useCallback(
    (href: string) => {
      const link = documentLink(href, baseKey);
      if (!link) return;
      if (link.kind === 'external') window.open(link.target, '_blank', 'noopener,noreferrer');
      else if (link.kind === 'route') navigate(link.target);
      else if (link.kind === 'anchor') document.getElementById(link.target.slice(1))?.scrollIntoView({ behavior: 'smooth' });
      else if (onOpenFile) onOpenFile(link.target);
      else if (projectId) {
        const rel = link.target.replace(`projects/${projectId}/`, '');
        const target = rel === 'plan.md' ? 'tab=plan' : rel.startsWith('docs/')
          ? `tab=docs&doc=${encodeURIComponent(rel)}` : `tab=files&file=${encodeURIComponent(link.target)}`;
        navigate(`/projects/${encodeURIComponent(projectId)}?${target}`);
      }
    },
    [navigate, onOpenFile, projectId, baseKey]
  );

  return (
    <Box
      onMouseUp={onMouseUp}
      sx={[markdownReadingSx, { maxWidth: hasDocumentVisuals(content) ? Math.max(maxWidth, 980) : maxWidth, mx: "auto" }, ...(Array.isArray(sx) ? sx : sx ? [sx] : [])] as SxProps<Theme>}
    >
      {meta && <FrontmatterHeader meta={meta} />}
      {sections.map((section) => {
        const mdBody = (
          <ReactMarkdown
            remarkPlugins={[remarkGfm]}
            urlTransform={(url) => {
              if (url.startsWith('wiki:')) {
                try { return documentLink(decodeURIComponent(url.slice(5)), baseKey) ? url : ''; } catch { return ''; }
              }
              return documentLink(url, baseKey) ? url : '';
            }}
            components={{
              pre: ({ node, children }) => {
                const code = node?.children[0];
                if (code?.type === 'element' && code.tagName === 'code') {
                  const language = visualLanguage(Array.isArray(code.properties.className) ? code.properties.className.join(' ') : String(code.properties.className ?? ''));
                  if (language) {
                    const source = code.children.map(child => child.type === 'text' ? child.value : '').join('').replace(/\n$/, '');
                    return <VisualBlock language={language} source={source} documentKey={baseKey} onNavigate={openDocumentLink} renderIssue={key => <JiraKeyChip issueKey={key}/>}/>;
                  }
                }
                return <pre>{children}</pre>;
              },
              h1: ({ children }) =>
                lockH2ToSectionSlug ? (
                  <h1>{enrichTextNodes(children, projectId, onOpenFile)}</h1>
                ) : (
                  <h1 id={slugifyHeading(String(children), used.current)}>
                    {enrichTextNodes(children, projectId, onOpenFile)}
                  </h1>
                ),
              h2: ({ children }) =>
                lockH2ToSectionSlug ? (
                  <h2 id={section.slug}>{enrichTextNodes(children, projectId, onOpenFile)}</h2>
                ) : (
                  <h2 id={section.slug}>
                    {enrichTextNodes(children, projectId, onOpenFile)}
                  </h2>
                ),
              h3: ({ children }) =>
                lockH2ToSectionSlug ? (
                  <h3>{enrichTextNodes(children, projectId, onOpenFile)}</h3>
                ) : (
                  <h3 id={slugifyHeading(String(children), used.current)}>
                    {enrichTextNodes(children, projectId, onOpenFile)}
                  </h3>
                ),
              p: ({ children }) => <p>{enrichTextNodes(children, projectId, onOpenFile)}</p>,
              li: ({ children }) => <li>{enrichTextNodes(children, projectId, onOpenFile)}</li>,
              a: ({ href, children }) => {
                const url = href ?? "";
                if (url.startsWith("wiki:")) {
                  const target = decodeURIComponent(url.slice(5));
                  const link = documentLink(target, baseKey);
                  return link ? <Box component="button" type="button" onClick={() => openDocumentLink(target)} sx={{ display: 'inline', p: 0, border: 0, background: 'none', font: 'inherit' }}><WikiFilePreview fileKey={link.target} label={String(children)}/></Box> : <>{children}</>;
                }
                const link = documentLink(url, baseKey);
                if (!link) return <>{children}</>;
                if (link.kind === 'file') return <Box component="button" type="button" onClick={() => openDocumentLink(url)} sx={{ display: 'inline', p: 0, border: 0, background: 'none', font: 'inherit' }}><WikiFilePreview fileKey={link.target} label={String(children) || url}/></Box>;
                if (link.kind === 'route') {
                  return (
                    <RouterLink to={link.target} style={{ color: c.tealInk }}>
                      {children}
                    </RouterLink>
                  );
                }
                return (
                  <a href={link.target} target={link.kind === 'external' ? '_blank' : undefined} rel="noopener noreferrer">
                    {children}
                  </a>
                );
              },
            }}
          >
            {section.body}
          </ReactMarkdown>
        );
        if (wrapSection) return <Box key={section.slug}>{wrapSection(section, mdBody)}</Box>;
        return (
          <Box key={section.slug} {...{ [sectionAttr]: section.slug }}>
            {mdBody}
          </Box>
        );
      })}
    </Box>
  );
}

/** Extract H2 headings for an on-this-page rail. */
export function markdownToc(content: string): Array<{ slug: string; title: string }> {
  const { body } = parsePlanFrontmatter(content);
  return splitSections(body).filter(section => /^##\s/m.test(section.body.split('\n')[0] ?? '')).map(section => ({ slug: section.slug, title: section.heading }));
}
